// /app/frontend/src/pages/app/TeamWorkspace.jsx
//
// Seat management dashboard for the Teams tier. Owners can:
//   - See current seats used vs. purchased
//   - Invite a new teammate by email + role
//   - Revoke a pending invite
//   - Promote / demote a member's role
//   - Remove a seat (eject)
// Non-owner members see a read-only roster.
// Non-Teams users see the upgrade prompt.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, UserPlus, Mail, Trash2, ShieldCheck, ShieldQuestion, Crown, X, Clock, Headphones } from "lucide-react";

export default function TeamWorkspace() {
  const [me, setMe] = useState(null);
  const [seats, setSeats] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    try {
      const meRes = await api.get("/workspace/me");
      setMe(meRes.data);
      if (meRes.data.tier === "team") {
        const [seatsRes, costsRes] = await Promise.all([
          api.get("/workspace/seats"),
          api.get("/workspace/seat-costs").catch(() => ({ data: null })),
        ]);
        setSeats(seatsRes.data);
        setCosts(costsRes.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load workspace.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div data-testid="workspace-loading" className="p-8 text-center text-[var(--ink-2)]">
        <Loader2 size={24} className="animate-spin mx-auto" />
      </div>
    );
  }

  const tier = me?.tier;
  const isOwner = me?.is_owner;

  // Non-Teams users hit the upgrade gate.
  if (tier !== "team") {
    return (
      <div data-testid="workspace-tier-gate" className="max-w-xl mx-auto py-12 px-6 text-center">
        <Users size={42} className="mx-auto text-amber-600 mb-4" />
        <h1 className="font-display font-extrabold text-3xl mb-2">Multi-seat workspaces are coming</h1>
        <p className="text-[var(--ink-2)] mb-6">
          The Teams tier lets you invite up to 5 teammates and share your lead pipeline, call recordings,
          AI Coach insights, and Power Team contacts. Currently in beta — join the waitlist below.
        </p>
        <a href="/upgrade" className="btn-money" data-testid="workspace-upgrade-cta">
          See Teams pricing
        </a>
      </div>
    );
  }

  return (
    <div data-testid="workspace-page" className="max-w-4xl mx-auto py-8 px-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="crumb">Team</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            {seats?.workspace?.name || "My Workspace"}
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Invite teammates so they share your lead pipeline, AI Coach, and call recordings.
          </p>
        </div>
        {isOwner && (
          <button
            type="button"
            className="btn btn-money"
            onClick={() => setInviteOpen(true)}
            disabled={(seats?.workspace?.seats_used + (seats?.invites?.length || 0)) >= seats?.workspace?.seats_purchased}
            data-testid="workspace-invite-btn"
          >
            <UserPlus size={14} /> Invite teammate
          </button>
        )}
      </div>

      {/* Seat-count meter */}
      {seats?.workspace && (
        <div className="card p-4 mb-5">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold uppercase tracking-wide text-[var(--ink-2)]">Seats</span>
            <span className="text-[var(--ink-2)]">
              {seats.workspace.seats_used} of {seats.workspace.seats_purchased} active
              {seats.invites.length > 0 && ` · ${seats.invites.length} pending`}
            </span>
          </div>
          <div className="h-2 bg-[var(--surface-2)] rounded-full overflow-hidden">
            <div
              className="h-full transition-all bg-emerald-500"
              style={{
                width: `${((seats.workspace.seats_used + seats.invites.length) / seats.workspace.seats_purchased) * 100}%`,
              }}
              data-testid="workspace-seats-bar"
            />
          </div>
        </div>
      )}

      {/* Seat-cost banner — surfaces overage line items + projected monthly add. */}
      {costs && (costs.overage.member > 0 || costs.overage.va > 0) && (
        <div
          data-testid="workspace-seat-costs"
          className="card p-4 mb-5 border-emerald-200 bg-emerald-50/60"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display font-bold text-base mb-1">
                Extra seats on this month&apos;s invoice
              </div>
              <div className="text-sm text-[var(--ink-2)] space-y-0.5">
                {costs.overage.member > 0 && (
                  <div data-testid="costs-line-member">
                    <b>{costs.overage.member}</b> additional team {costs.overage.member === 1 ? "seat" : "seats"} ·
                    {" "}${costs.price_per_role.member}/mo each
                  </div>
                )}
                {costs.overage.va > 0 && (
                  <div data-testid="costs-line-va">
                    <b>{costs.overage.va}</b> additional VA {costs.overage.va === 1 ? "seat" : "seats"} ·
                    {" "}${costs.price_per_role.va}/mo each
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-2)]">Next invoice</div>
              <div className="font-display font-extrabold text-2xl text-emerald-700"
                   data-testid="costs-monthly-total">
                +${costs.monthly_overage_usd}
                <span className="text-sm font-normal text-[var(--ink-2)]">/mo</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members */}
      <h2 className="font-display font-bold text-lg mb-3 mt-6">Members</h2>
      <div className="card divide-y divide-[var(--line)]" data-testid="workspace-members">
        {(seats?.members || []).map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            currentUserIsOwner={isOwner}
            isSelf={m.id === seats?.workspace?.owner_id && isOwner}
            onChanged={load}
          />
        ))}
      </div>

      {/* Pending invites */}
      {seats?.invites?.length > 0 && (
        <>
          <h2 className="font-display font-bold text-lg mb-3 mt-8">Pending invites</h2>
          <div className="card divide-y divide-[var(--line)]" data-testid="workspace-pending-invites">
            {seats.invites.map((inv) => (
              <PendingInviteRow key={inv.id} invite={inv} isOwner={isOwner} onChanged={load} />
            ))}
          </div>
        </>
      )}

      {inviteOpen && (
        <InviteModal
          onClose={() => setInviteOpen(false)}
          onCreated={() => { setInviteOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function MemberRow({ member, currentUserIsOwner, isSelf, onChanged }) {
  const [busy, setBusy] = useState(false);
  const role = member.workspace_role || "member";
  const isOwnerRole = role === "owner";

  const changeRole = async (newRole) => {
    setBusy(true);
    try {
      await api.patch(`/workspace/seats/${member.id}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change role.");
    } finally {
      setBusy(false);
    }
  };

  const removeSeat = async () => {
    if (!window.confirm(`Remove ${member.email} from the workspace? They'll keep their account but lose access to the shared pipeline.`)) return;
    setBusy(true);
    try {
      await api.delete(`/workspace/seats/${member.id}`);
      toast.success("Seat removed.");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove seat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 flex items-center gap-3" data-testid={`workspace-member-${member.id}`}>
      <div className="w-9 h-9 rounded-full bg-[var(--surface-2)] flex items-center justify-center text-sm font-bold text-[var(--ink-2)]">
        {(member.name || member.email).charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{member.name || member.email.split("@")[0]}</div>
        <div className="text-xs text-[var(--ink-2)] truncate">{member.email}</div>
      </div>
      <RoleBadge role={role} />
      {currentUserIsOwner && !isOwnerRole && (
        <div className="flex items-center gap-1">
          <select
            className="select text-xs"
            value={role}
            onChange={(e) => changeRole(e.target.value)}
            disabled={busy}
            data-testid={`workspace-member-role-${member.id}`}
          >
            <option value="admin">Admin</option>
            <option value="member">Member</option>
            <option value="va">VA (restricted)</option>
          </select>
          <button
            type="button"
            className="btn-ghost text-red-600 hover:bg-red-50"
            onClick={removeSeat}
            disabled={busy}
            title="Remove seat"
            data-testid={`workspace-member-remove-${member.id}`}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      )}
    </div>
  );
}

function PendingInviteRow({ invite, isOwner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/workspace/invites/${invite.id}`);
      toast.success("Invite revoked.");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke invite.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="p-4 flex items-center gap-3" data-testid={`workspace-invite-${invite.id}`}>
      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700">
        <Mail size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{invite.email}</div>
        <div className="text-xs text-[var(--ink-2)] flex items-center gap-1">
          <Clock size={10} /> Expires {new Date(invite.expires_at).toLocaleDateString()}
        </div>
      </div>
      <RoleBadge role={invite.role} />
      {isOwner && (
        <button
          type="button"
          className="btn-ghost text-red-600 hover:bg-red-50"
          onClick={revoke}
          disabled={busy}
          title="Revoke invite"
          data-testid={`workspace-invite-revoke-${invite.id}`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
        </button>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    owner: { Icon: Crown, label: "Owner", color: "bg-amber-100 text-amber-800 border-amber-200" },
    admin: { Icon: ShieldCheck, label: "Admin", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    member: { Icon: ShieldQuestion, label: "Member", color: "bg-sky-100 text-sky-800 border-sky-200" },
    va: { Icon: Headphones, label: "VA", color: "bg-violet-100 text-violet-800 border-violet-200" },
  };
  const cfg = map[role] || map.member;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${cfg.color}`}
          data-testid={`workspace-role-badge-${role}`}>
      <Icon size={9} /> {cfg.label}
    </span>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/workspace/invites", { email, role });
      toast.success(`Invite sent to ${email}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="workspace-invite-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display font-bold text-xl mb-1">Invite a teammate</h2>
        <p className="text-sm text-[var(--ink-2)] mb-5">
          They'll get an email with a link to join your workspace. The invite expires in 14 days.
        </p>
        <label className="field-label">Email</label>
        <input
          type="email"
          required
          className="input mb-4"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@gmail.com"
          data-testid="workspace-invite-email"
        />
        <label className="field-label">Role</label>
        <select
          className="select mb-2"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="workspace-invite-role"
        >
          <option value="member">Member — view and edit leads, calls, campaigns</option>
          <option value="admin">Admin — everything Member can + manage data</option>
          <option value="va">VA — restricted: no lead deletion, no contract sending</option>
        </select>
        {role === "va" && (
          <div className="text-xs bg-violet-50 border border-violet-200 text-violet-800 rounded-lg px-3 py-2 mb-6" data-testid="workspace-invite-va-hint">
            <b>Virtual Assistant seat.</b> Can run skip-traces, send SMS, dial out, and edit
            leads — but cannot delete leads or send/void DocuSign contracts. Perfect for
            off-shoring cold outreach without giving up control of signed deals.
          </div>
        )}
        {role !== "va" && <div className="mb-6" />}
        <div className="flex justify-end gap-3 pt-3 border-t border-[var(--line)]">
          <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-money" disabled={submitting || !email} data-testid="workspace-invite-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Send invite
          </button>
        </div>
      </form>
    </div>
  );
}
